import axios from 'axios';
import { apiClient } from '@/lib/api/api-client';
import type {
  CambioDeSolicitud,
  EstadoDeSolicitud,
  FichaDeSolicitud,
  InvitacionDeLaSolicitud,
  ListaDeSolicitudes,
  NuevaSolicitud,
} from '../types/solicitudes.types';

/*
  Landing nueva (vault 346, §5). Va por `apiClient` (ruta relativa, el mismo origen que la app): nada de
  direcciones de servidor escritas en el código.
*/

/** Por qué no se pudo enviar una solicitud (la pantalla elige el texto). */
export type FalloDelEnvio =
  | { tipo: 'DATOS'; campos: Record<string, string> }
  | { tipo: 'LIMITE' }
  | { tipo: 'RED' };

export class ErrorAlEnviarLaSolicitud extends Error {
  constructor(public readonly fallo: FalloDelEnvio) {
    super(fallo.tipo);
    this.name = 'ErrorAlEnviarLaSolicitud';
  }
}

/** El código que el servidor pone en su 429: la pantalla lo explica (no el aviso global de «Reintentar»). */
export const CODIGO_DE_DEMASIADAS_SOLICITUDES = 'DEMASIADAS_SOLICITUDES';

/**
 * `POST /api/public/solicitudes-mini-prixia`. Resuelve SÓLO con un 2xx real (el servidor responde 202
 * siempre que el cuerpo es válido). 400 → `DATOS` con un mensaje por campo; 429 → `LIMITE`; lo demás
 * (sin conexión, 413, 5xx) → `RED`: la pantalla conserva lo escrito y ofrece reintentar.
 */
export async function enviarSolicitudDeMiniPrixia(datos: NuevaSolicitud): Promise<void> {
  try {
    await apiClient.post('/api/public/solicitudes-mini-prixia', datos);
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const status = e.response?.status;
      if (status === 400) {
        const campos = (e.response?.data as { campos?: Record<string, string> })?.campos ?? {};
        throw new ErrorAlEnviarLaSolicitud({ tipo: 'DATOS', campos });
      }
      if (status === 429) throw new ErrorAlEnviarLaSolicitud({ tipo: 'LIMITE' });
    }
    throw new ErrorAlEnviarLaSolicitud({ tipo: 'RED' });
  }
}

/** Mide cuánto lleva abierto un formulario: se crea al abrirlo y se lee al enviar. */
export function cronometroDelFormulario(ahora: () => number = () => performance.now()) {
  const inicio = ahora();
  return () => Math.max(0, Math.round(ahora() - inicio));
}

// ─────────────────────────── panel de plataforma ───────────────────────────

const BASE = '/api/admin/solicitudes';

export const solicitudesService = {
  listar(filtros: { estado?: EstadoDeSolicitud | ''; buscar?: string; pagina?: number }) {
    const params: Record<string, string | number> = {};
    if (filtros.estado) params.estado = filtros.estado;
    if (filtros.buscar?.trim()) params.buscar = filtros.buscar.trim();
    if (filtros.pagina && filtros.pagina > 1) params.pagina = filtros.pagina;
    return apiClient.get<ListaDeSolicitudes>(BASE, { params });
  },
  ficha(id: string) {
    return apiClient.get<FichaDeSolicitud>(`${BASE}/${id}`);
  },
  cambiar(id: string, cambio: CambioDeSolicitud) {
    return apiClient.patch<FichaDeSolicitud>(`${BASE}/${id}`, cambio);
  },
  invitar(id: string) {
    return apiClient.post<InvitacionDeLaSolicitud>(`${BASE}/${id}/invitacion`);
  },
  borrarDatos(id: string) {
    return apiClient.post<FichaDeSolicitud>(`${BASE}/${id}/borrar-datos`);
  },
};
